import { Translation } from '@ps/web-shared-components';
import classNames from 'clsx';
import React from 'react';
import type { CellProps, Column } from 'react-table';

import { Tooltip, TournamentStateTranslations } from '@swf/poker';

import useCheckUserClubRole from '../../../hooks/useCheckUserClubRole';
import { TileType, UserClubRole } from '../../../types/common';
import type { HGPokerGameTile } from '../../../types/common';
import { HandTypeTranslations, PotTypeTranslations } from '../../../utils/games';
import SelectGame from '../../GameTile/SelectGame/SelectGame';
import RecurringIcon from '../../GameTile/assets/recurring.svg';
import VideoIcon from '../../GameTile/assets/video.svg';
import StartTime from '../StartTime/StartTime';

import {
  TXTCLI_Buy_In,
  TXTCLI_Enrolled,
  TXTCLI_Game,
  TXTCLI_Limit,
  TXTCLI_Plrs,
  TXTCLI_Recurring_Tournament,
  TXTCLI_Stakes,
  TXTCLI_Start,
  TXTCLI_State_status,
  TXTCLI_Table,
  TXTCLI_Tournament,
  TXTCLI_Video_Chat_Enabled
} from './Columns.messages.json';
import styles from './Columns.scss';

export type TournamentTile = HGPokerGameTile & {
  buyIn?: string;
  registered?: boolean;
  whenStart?: string;
  players: JSX.Element;
  stakes?: React.ReactNode;
};

export const tournamentColumns: Column<TournamentTile>[] = [
  {
    Header: <Translation {...TXTCLI_Start} />,
    accessor: 'whenStart',
    Cell: ({ value, row }: CellProps<TournamentTile, string | undefined>) => {
      const canSelect = useCheckUserClubRole(row.original.clubId, [
        UserClubRole.Manager,
        UserClubRole.Admin
      ]);
      return (
        <div className={styles.noWrap}>
          {canSelect && row.original.type !== TileType.Recent ? (
            <SelectGame id={row.original.id} listView />
          ) : null}
          <StartTime startDateTime={value} />
        </div>
      );
    }
  },
  { Header: <Translation {...TXTCLI_Tournament} />, accessor: 'name' },
  {
    Header: <Translation {...TXTCLI_Game} />,
    accessor: 'handType',
    Cell: ({ value = '' }: CellProps<TournamentTile, string | undefined>) => (
      <Translation {...HandTypeTranslations[value]} />
    )
  },
  {
    Header: <Translation {...TXTCLI_Limit} />,
    accessor: 'potStruct',
    Cell: ({ value = '' }: CellProps<TournamentTile, string | undefined>) => (
      <Translation {...PotTypeTranslations[value]} />
    )
  },
  { Header: <Translation {...TXTCLI_Buy_In} />, accessor: 'buyIn' },
  {
    Header: <Translation {...TXTCLI_State_status} />,
    accessor: 'tournamentState',
    Cell: ({ value = '' }: CellProps<TournamentTile, string | undefined>) => (
      <Translation {...TournamentStateTranslations[value]} />
    )
  },
  {
    Header: <Translation {...TXTCLI_Enrolled} />,
    accessor: 'entrants'
  },
  {
    Header: '',
    accessor: () => ['hasVideoSupport', 'isRecurring'],
    id: 'featuresIconsColumn',
    Cell: ({ row, value }: CellProps<TournamentTile, boolean>) => {
      const hasVideoSupport = row.original?.hasVideoSupport;
      const isRecurring = row.original?.isRecurring;

      if (value) {
        return (
          <div className={styles.iconRow}>
            <div
              data-testid="tournament-recurring-icon"
              className={classNames(styles.icon, !isRecurring ? styles.iconDisabled : null)}
            >
              {isRecurring ? (
                <Tooltip
                  content={<Translation {...TXTCLI_Recurring_Tournament} />}
                  arrowClassName={styles.tooltipArrow}
                  tooltipClassName={styles.tooltip}
                  placement="left"
                  refElementClassName={styles.tooltipWrapper}
                >
                  <RecurringIcon />
                </Tooltip>
              ) : (
                <RecurringIcon />
              )}
            </div>
            <div
              data-testid="tournament-video-icon"
              className={classNames(styles.icon, !hasVideoSupport ? styles.iconDisabled : null)}
            >
              {hasVideoSupport ? (
                <Tooltip
                  content={<Translation {...TXTCLI_Video_Chat_Enabled} />}
                  arrowClassName={styles.tooltipArrow}
                  tooltipClassName={styles.tooltip}
                  placement="left"
                  refElementClassName={styles.tooltipWrapper}
                >
                  <VideoIcon />
                </Tooltip>
              ) : (
                <VideoIcon />
              )}
            </div>
          </div>
        );
      }

      return null;
    }
  }
];

export const cashGameColumns: Column<TournamentTile>[] = [
  {
    Header: <Translation {...TXTCLI_Table} />,
    accessor: 'name',
    Cell: ({ value, row }: CellProps<TournamentTile, string | undefined>) => {
      const canSelect = useCheckUserClubRole(row.original.clubId, [
        UserClubRole.Manager,
        UserClubRole.Admin
      ]);
      return (
        <>
          {canSelect ? <SelectGame id={row.original.id} listView /> : null}
          {value}
        </>
      );
    }
  },
  {
    Header: <Translation {...TXTCLI_Game} />,
    accessor: 'handType',
    Cell: ({ value = '' }: CellProps<TournamentTile, string | undefined>) => (
      <Translation {...HandTypeTranslations[value]} />
    )
  },
  {
    Header: <Translation {...TXTCLI_Limit} />,
    accessor: 'potStruct',
    Cell: ({ value = '' }: CellProps<TournamentTile, string | undefined>) => (
      <Translation {...PotTypeTranslations[value]} />
    )
  },
  { Header: <Translation {...TXTCLI_Stakes} />, accessor: 'stakes' },
  {
    Header: <Translation {...TXTCLI_Plrs} />,
    accessor: 'players'
  },
  {
    Header: '',
    accessor: 'hasVideoSupport',
    Cell: ({ value }) => (
      <div className={classNames(styles.icon, !value ? styles.iconDisabled : null)}>
        {value ? (
          <Tooltip
            content={<Translation {...TXTCLI_Video_Chat_Enabled} />}
            arrowClassName={styles.tooltipArrow}
            tooltipClassName={styles.tooltip}
            placement="left"
            refElementClassName={styles.tooltipWrapper}
          >
            <VideoIcon />
          </Tooltip>
        ) : (
          <VideoIcon />
        )}
      </div>
    )
  }
];
